import { buildLatestCombinedRecord, fetchLabDataByNameFallbacks as sharedFetchLabDataByNameFallbacks, getLabRawValue } from './labRecordUtils';
import { STATUS_COLORS, getStatusHex, getWorstStatusColor } from './healthStatusColors';

const toNumericValue = (value) => {
  const parsed = parseFloat(String(value ?? '').replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const hasDisplayValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== '' && String(value).trim() !== '-';

const buildFieldSummary = async ({ rawValue, formula, age, gender, decimals = 1, valueSuffix = '', fallbackStatus = 'ปกติ' }) => {
  if (!hasDisplayValue(rawValue)) {
    return {
      value: '-',
      status: 'ไม่มีข้อมูล',
      color: STATUS_COLORS.gray,
      rawValue: null,
    };
  }

  const numericValue = toNumericValue(rawValue);
  if (numericValue === null) {
    const textValue = String(rawValue).trim();
    return {
      value: textValue,
      status: textValue,
      color: STATUS_COLORS.gray,
      rawValue: textValue,
    };
  }

  const formulaResult = formula ? await formula(numericValue, age, gender) : null;
  const color = formulaResult?.color ? getStatusHex(formulaResult.color) : STATUS_COLORS.green;

  return {
    value: `${String(rawValue).trim()}${valueSuffix}`,
    status: formulaResult?.title || fallbackStatus,
    color,
    rawValue: numericValue,
  };
};

const buildRangeSummary = ({ rawValue, min, max, decimals = 1, valueSuffix = '%', lowTitle = 'ต่ำ', highTitle = 'สูง', normalTitle = 'ปกติ' }) => {
  if (!hasDisplayValue(rawValue)) {
    return {
      value: '-',
      status: 'ไม่มีข้อมูล',
      color: STATUS_COLORS.gray,
      rawValue: null,
    };
  }

  const numericValue = toNumericValue(rawValue);
  if (numericValue === null) {
    const textValue = String(rawValue).trim();
    return {
      value: textValue,
      status: textValue,
      color: STATUS_COLORS.gray,
      rawValue: textValue,
    };
  }

  if (numericValue < min) {
    return {
      value: `${numericValue.toFixed(decimals)}${valueSuffix}`,
      status: lowTitle,
      color: STATUS_COLORS.red,
      rawValue: numericValue,
    };
  }

  if (numericValue > max) {
    return {
      value: `${numericValue.toFixed(decimals)}${valueSuffix}`,
      status: highTitle,
      color: STATUS_COLORS.red,
      rawValue: numericValue,
    };
  }

  return {
    value: `${numericValue.toFixed(decimals)}${valueSuffix}`,
    status: normalTitle,
    color: STATUS_COLORS.green,
    rawValue: numericValue,
  };
};

export const CBC_KEY_GROUPS = {
  rbc: ['rbc', 'cbcrbc', 'cbcrbcresult'],
  hemoglobin: ['hb', 'hemoglobin', 'cbchbresult'],
  hematocrit: ['hct', 'hematocrit', 'cbchctresult'],
  wbc: ['wbc', 'cbcwbcresult', 'cbcwbc'],
  platelet: [
    'platelet',
    'platelets',
    'cbcpltresult',
    'cbcplateletresult',
    'cbcplatelet',
  ],
  plateletCount: [
    'plt',
    'platelet_count',
    'plateletcount',
    'plateletcountresult',
    'plateletscount',
    'plateletscountresult',
    'cbcpltcountresult',
    'cbcpltcount',
    'cbc plt count result',
    'cbc plt count',
    'cbcplateletcount',
  ],
  mcv: ['mcv', 'cbcmcvresult'],
  mch: ['mch', 'cbcmchresult'],
  mchc: ['mchc', 'cbcmchcresult'],
  neutrophile: ['neutrophile', 'cbcneutrophileresult', 'neutrophile%', 'neutrophile_percent'],
  lymphocyte: ['lymphocyte-count', 'lymphocyte_count', 'cbclymphocyteresult', 'lymphocyte%', 'lymphocyte_percent'],
  monocyte: ['monocyte-count', 'monocyte_count', 'cbcmonocyteresult', 'monocyte%', 'monocyte_percent'],
  basophil: [
    'basophil-count',
    'basophil_count',
    'cbcbasophileresult',
    'basophil%',
    'basophil_percent',
    'basophile',
    'basophilecount',
    'basophile_percent',
    'cbcbasophile',
    'cbcbasophilecount',
  ],
  eosinophil: ['eosinophil', 'cbceosinophileresult', 'cbceosinophilresult', 'cbceosinophilcount', 'cbceosinophile', 'cbceosinophilecount', 'eosinophil%', 'eosinophil_percent', 'eosinophile%', 'eosinophile_percent', 'eosinophil-count', 'eosinophil_count'],
  rdw: ['rdw', 'cbcrdw', 'cbcrdwresult'],
  mpv: ['mpv', 'cbcmpv', 'cbcmpvresult'],
};

export const CBC_ADDITIONAL_KEY_GROUPS = {
  rbcMorphology: ['rbc_morphology', 'rbcmorphology', 'rbcmorph', 'rbcmorphologyresult', 'cbcrbcmorphologyresult'],
  atypicalLymphocyte: ['atypical_lymphocyte', 'atypicallymphocyte', 'cbcatypicallymphocyte', 'cbcatypicallymphocyteresult'],
  anisocytosis: ['anisocytosis', 'cbcanisocytosisresult'],
  bandForm: ['bandform', 'band_form', 'bandformresult', 'cbcbandformresult'],
  hypochromic: ['hypochromic', 'cbchypochromicresult'],
  spherocyte: ['spherocyte', 'cbcspherocyte', 'cbcspherocyteresult'],
  otherBlood: ['other_blood', 'otherblood', 'otherbloodresult', 'cbcother', 'cbcotherresult'],
  myeloblast: ['myeloblast', 'cbcmyeloblast', 'cbcmyeloblastresult'],
  promyelocyte: ['promyelocyte', 'cbcpromyelocyte', 'cbcpromyelocyteresult'],
  myelocyte: ['myelocyte', 'cbcmyelocyte', 'cbcmyelocyteresult'],
  metamyelocyte: ['metamyelocyte', 'cbcmetamyelocyte', 'cbcmetamyelocyteresult'],
  lymphoblast: ['lymphoblast', 'cbclymphoblast', 'cbclymphoblastresult'],
  macrocyte: ['macrocyte', 'cbcmacrocyteresult'],
  poikilocytosis: ['poikilocytosis', 'cbcpoikilocytosisresult'],
  ovalocyte: ['ovalocyte', 'cbcovalocyteresult'],
  targetCell: ['targetcell', 'target_cell', 'cbctargetcellresult'],
  schistocyte: ['schistocyte', 'cbcschistocyteresult'],
  tearDrop: ['teardrop', 'teardropcell', 'tear_drop', 'teardropresult', 'teardropcellresult', 'cbcteardropresult'],
  burrCell: ['burrcell', 'burr_cell', 'cbcburrcellresult'],
  polychromasia: ['polychromasia', 'cbcpolychromasiaresult'],
  blastCell: ['blastcell', 'blast_cell', 'blast', 'cbcblastresult'],
  nrbc: ['nrbc', 'cbcnrbcresult'],
  microcyte: ['microcyte', 'cbcmicrocyteresult', 'cbcmicrocyte'],
};

export const CBC_FETCH_KEY_GROUPS = {
  ...CBC_KEY_GROUPS,
  ...CBC_ADDITIONAL_KEY_GROUPS,
};

export const fetchCbcLabGroupsWithFallbacks = async (getLabDataByName, keyGroups = CBC_FETCH_KEY_GROUPS) => {
  const entries = Object.entries(keyGroups || {});
  const fetched = await Promise.all(
    entries.map(async ([field, candidates]) => {
      const response = await sharedFetchLabDataByNameFallbacks(getLabDataByName, candidates);
      return [field, response];
    }),
  );

  return Object.fromEntries(fetched);
};

export const collectLabItems = (labData, keys = []) => {
  const collected = [];
  keys.forEach((key) => {
    if (labData?.[key]?.length > 0) {
      collected.push(...labData[key]);
    }
  });
  return collected;
};

export const toDbFormat = (items = []) =>
  (items || []).map((item) => ({
    value: item?.value,
    dateadd: item?.date,
    campaignid: item?.campaignid,
    memberid: item?.memberid,
  }));

export const mergeUniqueItems = (baseItems = [], extraItems = []) => {
  const seen = new Set(
    (baseItems || []).map((item) => `${item?.dateadd || ''}|${item?.value ?? ''}|${item?.campaignid || ''}|${item?.memberid || ''}`),
  );
  const merged = [...(baseItems || [])];

  (extraItems || []).forEach((item) => {
    const key = `${item?.dateadd || ''}|${item?.value ?? ''}|${item?.campaignid || ''}|${item?.memberid || ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(item);
    }
  });

  return merged.sort((a, b) => new Date(b?.dateadd || 0) - new Date(a?.dateadd || 0));
};

export const mergeCbcItemsWithMemberLab = ({
  rbcItems = [],
  hemoglobinItems = [],
  hematocritItems = [],
  wbcItems = [],
  plateletItems = [],
  plateletCountItems = [],
  mcvItems = [],
  mchItems = [],
  mchcItems = [],
  neutrophileItems = [],
  lymphocyteItems = [],
  monocyteItems = [],
  basophilItems = [],
  eosinophilItems = [],
  rdwItems = [],
  mpvItems = [],
  rbcMorphologyItems = [],
  additionalItems = {},
  memberLabData = null,
}) => {
  const labData = memberLabData?.labData;
  if (!labData) {
    return {
      rbcItems,
      hemoglobinItems,
      hematocritItems,
      wbcItems,
      plateletItems,
      plateletCountItems,
      mcvItems,
      mchItems,
      mchcItems,
      neutrophileItems,
      lymphocyteItems,
      monocyteItems,
      basophilItems,
      eosinophilItems,
      rdwItems,
      mpvItems,
      rbcMorphologyItems,
      additionalItems,
    };
  }

  return {
    rbcItems: mergeUniqueItems(rbcItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.rbc))),
    hemoglobinItems: mergeUniqueItems(hemoglobinItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.hemoglobin))),
    hematocritItems: mergeUniqueItems(hematocritItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.hematocrit))),
    wbcItems: mergeUniqueItems(wbcItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.wbc))),
    plateletItems: mergeUniqueItems(plateletItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.platelet))),
    plateletCountItems: mergeUniqueItems(plateletCountItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.plateletCount))),
    mcvItems: mergeUniqueItems(mcvItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.mcv))),
    mchItems: mergeUniqueItems(mchItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.mch))),
    mchcItems: mergeUniqueItems(mchcItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.mchc))),
    neutrophileItems: mergeUniqueItems(neutrophileItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.neutrophile))),
    lymphocyteItems: mergeUniqueItems(lymphocyteItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.lymphocyte))),
    monocyteItems: mergeUniqueItems(monocyteItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.monocyte))),
    basophilItems: mergeUniqueItems(basophilItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.basophil))),
    eosinophilItems: mergeUniqueItems(eosinophilItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.eosinophil))),
    rdwItems: mergeUniqueItems(rdwItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.rdw))),
    mpvItems: mergeUniqueItems(mpvItems, toDbFormat(collectLabItems(labData, CBC_KEY_GROUPS.mpv))),
    rbcMorphologyItems: mergeUniqueItems(rbcMorphologyItems, toDbFormat(collectLabItems(labData, CBC_ADDITIONAL_KEY_GROUPS.rbcMorphology || []))),
    additionalItems,
  };
};

// Merges the differential/morphology CBC fields (Atypical Lymphocyte, Band form, Ovalocyte, etc.)
// that aren't handled by mergeCbcItemsWithMemberLab. Keyed by CBC_ADDITIONAL_KEY_GROUPS field name.
export const mergeAdditionalCbcItems = (itemsByField = {}, memberLabData = null) => {
  const labData = memberLabData?.labData;
  const merged = {};

  Object.entries(CBC_ADDITIONAL_KEY_GROUPS).forEach(([field, keys]) => {
    const baseItems = itemsByField[field] || [];
    merged[field] = labData
      ? mergeUniqueItems(baseItems, toDbFormat(collectLabItems(labData, keys)))
      : baseItems;
  });

  return merged;
};

export const buildCbcStatusSummary = async ({
  rbcItems = [],
  hemoglobinItems = [],
  hematocritItems = [],
  wbcItems = [],
  plateletItems = [],
  plateletCountItems = [],
  mcvItems = [],
  mchItems = [],
  mchcItems = [],
  neutrophileItems = [],
  lymphocyteItems = [],
  monocyteItems = [],
  basophilItems = [],
  eosinophilItems = [],
  age = 0,
  gender = '',
  formulas = {},
}) => {
  const latestRecord = buildLatestCombinedRecord(
    [
      { field: 'rbc', items: rbcItems },
      { field: 'hemoglobin', items: hemoglobinItems },
      { field: 'hematocrit', items: hematocritItems },
      { field: 'wbc', items: wbcItems },
      { field: 'platelet', items: plateletItems },
      { field: 'plateletCount', items: plateletCountItems },
      { field: 'mcv', items: mcvItems },
      { field: 'mch', items: mchItems },
      { field: 'mchc', items: mchcItems },
      { field: 'neutrophile', items: neutrophileItems },
      { field: 'lymphocyte', items: lymphocyteItems },
      { field: 'monocyte', items: monocyteItems },
      { field: 'basophil', items: basophilItems },
      { field: 'eosinophil', items: eosinophilItems },
    ],
    getLabRawValue,
  );

  const latestValues = {
    rbc: await buildFieldSummary({ rawValue: latestRecord?.rbc, formula: formulas.rbc, age, gender, decimals: 2, valueSuffix: ' x10^6/µL' }),
    hemoglobin: await buildFieldSummary({ rawValue: latestRecord?.hemoglobin, formula: formulas.hb, age, gender, decimals: 1, valueSuffix: ' g/dL' }),
    hematocrit: await buildFieldSummary({ rawValue: latestRecord?.hematocrit, formula: formulas.hct, age, gender, decimals: 1, valueSuffix: ' %' }),
    wbc: await buildFieldSummary({ rawValue: latestRecord?.wbc, formula: formulas.wbc, age, gender, decimals: 1, valueSuffix: ' x10^3/µL' }),
    platelet: await buildFieldSummary({ rawValue: latestRecord?.platelet, formula: formulas.platelet, age, gender, decimals: 0, valueSuffix: ' x10^3/µL' }),
    plateletCount: await buildFieldSummary({ rawValue: latestRecord?.plateletCount, formula: formulas.plateletCount || formulas.platelet, age, gender, decimals: 0, valueSuffix: ' x10^3/µL' }),
    mcv: await buildFieldSummary({ rawValue: latestRecord?.mcv, formula: formulas.mcv, age, gender, decimals: 1, valueSuffix: ' fL' }),
    mch: await buildFieldSummary({ rawValue: latestRecord?.mch, formula: formulas.mch, age, gender, decimals: 1, valueSuffix: ' pg' }),
    mchc: await buildFieldSummary({ rawValue: latestRecord?.mchc, formula: formulas.mchc, age, gender, decimals: 1, valueSuffix: ' g/dL' }),
    neutrophile: buildRangeSummary({ rawValue: latestRecord?.neutrophile, min: 46.5, max: 75, decimals: 1, valueSuffix: ' %', lowTitle: 'ต่ำ', highTitle: 'สูง', normalTitle: 'ปกติ' }),
    lymphocyte: buildRangeSummary({ rawValue: latestRecord?.lymphocyte, min: 12, max: 44, decimals: 1, valueSuffix: ' %', lowTitle: 'ต่ำ', highTitle: 'สูง', normalTitle: 'ปกติ' }),
    monocyte: buildRangeSummary({ rawValue: latestRecord?.monocyte, min: 0, max: 11.2, decimals: 1, valueSuffix: ' %', lowTitle: 'ต่ำ', highTitle: 'สูง', normalTitle: 'ปกติ' }),
    basophil: buildRangeSummary({ rawValue: latestRecord?.basophil, min: 0, max: 2.5, decimals: 1, valueSuffix: ' %', lowTitle: 'ต่ำ', highTitle: 'สูง', normalTitle: 'ปกติ' }),
    eosinophil: buildRangeSummary({ rawValue: latestRecord?.eosinophil, min: 0, max: 9.5, decimals: 1, valueSuffix: ' %', lowTitle: 'ต่ำ', highTitle: 'สูง', normalTitle: 'ปกติ' }),
  };

  const CARD_STATUS_FIELDS = ['hemoglobin', 'wbc', 'plateletCount'];
  const cardStatusValues = CARD_STATUS_FIELDS.map((field) => latestValues[field]);
  const hasData = cardStatusValues.some((item) => hasDisplayValue(item.value));
  const cardColor = hasData
    ? getWorstStatusColor(cardStatusValues.map((item) => item.color))
    : STATUS_COLORS.gray;

  return {
    latestRecord,
    latestVisitDate: latestRecord?.rawDate || latestRecord?.date || '',
    latestValues,
    hasData,
    cardColor,
  };
};
